import { Button, Typography } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { endOfYear, format, startOfYear } from 'date-fns';
import { nl } from 'date-fns/locale';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    formatDateString,
    mapDateToApiDate,
    Page,
    PermissionKeys,
    RowActions,
    SortOrder,
    Table,
    useHasPermission,
    useQueryParams,
} from '../../../shared';
import { ClosedDaysFilter } from '../../components';
import { useClosedDaysList, useDeleteClosedDay } from '../../hooks';
import { IClosedDay, IClosedDaysFilter } from '../../models';

export const ClosedDaysPage: FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.CLOSED_DAYS_WRITE);
    const [filter, setFilter] = useState<IClosedDaysFilter>({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
    });

    const { page, setPage, pageSize, setPageSize, sort, setSort } = useQueryParams({
        defaultSort: [{ field: 'date', sort: 'asc' }],
    });

    const { data, isPending } = useClosedDaysList({
        page,
        pageSize,
        sortBy: sort[0]?.field,
        sortOrder: sort[0]?.sort?.toUpperCase() as SortOrder,
        startDate: mapDateToApiDate(filter.startDate) || undefined,
        endDate: mapDateToApiDate(filter.endDate) || undefined,
    });

    const { mutateAsync: deleteClosedDay } = useDeleteClosedDay();

    const columns: GridColDef[] = [
        {
            field: 'date',
            headerName: t('date'),
            minWidth: 150,
            flex: 0.5,
            valueFormatter: (value: string) => formatDateString(value),
        },
        {
            field: 'dayOfWeek',
            headerName: t('day'),
            minWidth: 150,
            flex: 0.5,
            sortable: false,
            valueGetter: (_, row: IClosedDay) => format(new Date(row.date), 'EEEE', { locale: nl }),
        },
        {
            field: 'description',
            headerName: t('description'),
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row }: GridCellParams<IClosedDay>) =>
                hasWritePermission && (
                    <RowActions
                        onDelete={() => deleteClosedDay(row.id)}
                        deleteWarningTitle={t('closedDayDeleteWarningTitle')}
                        deleteWarningText={
                            <>
                                {t('closedDayDeleteWarningText')}
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    {formatDateString(row.date)} {row.description}
                                </Typography>
                            </>
                        }
                    />
                ),
        },
    ];

    return (
        <Page
            title={t('closedDays')}
            filter={<ClosedDaysFilter filter={filter} onChange={setFilter} />}
            actions={[
                hasWritePermission && (
                    <Button component={Link} to="/admin/closed-days/new" color="primary" variant="contained">
                        {t('newClosedDays')}
                    </Button>
                ),
            ]}
        >
            <Table
                rows={data?.data || []}
                columns={columns}
                rowCount={data?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                prefix="closed-days"
            />
        </Page>
    );
};
